<script lang="ts" setup>
import { computed } from 'vue'
import { createGAEvent, GaGeneralEvent } from '@/assets/ts/customEvent'
import { getCurrentGaPageName, getVueAssetImagePath } from '@/lib/utils'
import { isRegionSupportBuyforyou, isGlobalSite } from '@/lib/checkService'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'

enum ActionButtonId {
	AddShipment = 'add-shipment',
	AddProxyRequest = 'add-proxy-request',
}

interface ActionButton {
	id: string
	title: string
	link: string
	icon: string
	bgColor: string
	hoverColor: string
	gaEvent: string
}

interface MemberCenterData {
	link: string
	greeting: string
	addShipment: Omit<
		ActionButton,
		'id' | 'icon' | 'bgColor' | 'hoverColor' | 'gaEvent'
	>
	addProxyOrders: Omit<
		ActionButton,
		'id' | 'icon' | 'bgColor' | 'hoverColor' | 'gaEvent'
	>
}

const props = defineProps<{
	data: MemberCenterData
}>()

const displayedGreeting = computed(() => {
	return props.data.greeting.replace(
		'{fullName}',
		window.BUYANDSHIP.settings.user_full_name || '',
	)
})

const actionButtons = computed<ActionButton[]>(() => {
	const buttons: ActionButton[] = []

	const isInGlobalSite = isGlobalSite()
	if (!isInGlobalSite) {
		buttons.push({
			id: ActionButtonId.AddShipment,
			...props.data.addShipment,
			icon: 'add_shipment.svg',
			bgColor: 'bg-o-75',
			hoverColor: 'hover:outline-o-500',
			gaEvent: '',
		})
	}

	if (isRegionSupportBuyforyou.value) {
		buttons.push({
			id: ActionButtonId.AddProxyRequest,
			...props.data.addProxyOrders,
			icon: 'add_proxy_orders.svg',
			bgColor: 'bg-t-75',
			hoverColor: 'hover:outline-t-500',
			gaEvent: '',
		})
	}
	return buttons
})

function sendMemberCenterGaEvent() {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		button_name: 'User Dropdown Menu Member Center',
		page_name: getCurrentGaPageName(),
	})
}

function sendAddShipmentGaEvent() {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		button_name: 'User Dropdown Menu Add Shipment',
		page_name: getCurrentGaPageName(),
	})
}

function sendAddProxyRequestGaEvent() {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		button_name: 'User Dropdown Menu Add Proxy Request',
		page_name: getCurrentGaPageName(),
	})
}
</script>

<template>
	<div
		class="member-center-card my-good-friends flex flex-col gap-close-ish-friends lg:my-0 lg:mb-[12px] lg:gap-[12px]"
	>
		<!-- Greeting Section -->
		<a
			class="greeting-section group flex cursor-pointer items-center justify-between gap-8px rounded-[12px] bg-n-50 p-close-friends"
			:href="data.link"
			@click="sendMemberCenterGaEvent()"
		>
			<div class="typography-s1-reg truncate text-n-800 group-hover:text-o-500">
				{{ displayedGreeting }}
			</div>
			<BaseIcon name="chevron_thin" class="h-16px w-16px shrink-0 fill-n-950" />
		</a>

		<!-- Action Buttons Section -->
		<div class="action-buttons flex gap-[12px]">
			<a
				v-for="button in actionButtons"
				:key="button.title"
				class="action-button flex-1 cursor-pointer rounded-[12px] p-close-friends hover:outline hover:outline-1"
				:class="[button.bgColor, button.hoverColor]"
				:href="button.link"
				@click="
					button.id === ActionButtonId.AddShipment
						? sendAddShipmentGaEvent()
						: sendAddProxyRequestGaEvent()
				"
			>
				<div class="flex flex-col gap-strong-friends">
					<div
						class="icon-container flex h-[50px] w-[50px] items-center justify-center"
					>
						<img
							:src="getVueAssetImagePath(button.icon)"
							:alt="`${button.title} icon`"
							loading="lazy"
						/>
					</div>
					<div
						class="button-title typography-s1-emp mdp:typography-s2-emp text-n-800"
					>
						{{ button.title }}
					</div>
				</div>
			</a>
		</div>
	</div>
</template>
